import type { AvatarSlotsAndSlotProps } from '@mui/joy/Avatar';
import DefaultAvatar from '@mui/joy/Avatar';
import _capitalize from 'lodash/capitalize';
import _trim from 'lodash/trim';
import { forwardRef, useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { avatarContainerVariants, avatarTextVariants, getAvatarRootStyles } from './styles';
import type { AvatarProps } from './types';

const getUserInitials = (name: string, type: 'single' | 'double'): string | null => {
  const trimmedName = _trim(name);

  if (!trimmedName) {
    return null;
  }

  if (type === 'double') {
    const names = trimmedName.split(/\s+/);

    if (names.length > 1) {
      return `${_capitalize(names[0][0])}${_capitalize(names[names.length - 1][0])}`;
    }

    return trimmedName.substring(0, 2).toUpperCase();
  }

  return trimmedName[0].toUpperCase();
};

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(function Avatar(props, ref) {
  const {
    color = 'primary',
    children,
    name,
    size = 'sm',
    className,
    initialsType = 'single',
    slotProps: _slotProps,
    ...rest
  } = props;

  const slotProps = useMemo<AvatarSlotsAndSlotProps['slotProps']>(
    () => ({
      root: {
        className: slowCn(
          avatarContainerVariants({ color, size }),
          avatarTextVariants({ color, size }),
          className,
        ),
        style: {
          ...getAvatarRootStyles(color),
          ..._slotProps?.root?.style,
        },
      },
      img: { className: 'h-full w-full' },
    }),
    [className, color, size, _slotProps?.root?.style],
  );

  return (
    <DefaultAvatar alt={name} {...rest} ref={ref} slotProps={slotProps}>
      {children ?? (name ? getUserInitials(name, initialsType) : null)}
    </DefaultAvatar>
  );
});

export default Avatar;
